import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import AmplifierForm from './AmplifierForm';
import AmplifierList from './AmplifierList';
import axios from 'axios';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(config => {
  const token = safeStorage.getItem('authToken');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});
const manufacturerApi = axios.create({ baseURL: API_URL });
const templatesApi = axios.create({ baseURL: API_URL });

const AmplifierScreen = () => {
  const { isReadOnly } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [amplifiers, setAmplifiers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [parentAmplifiers, setParentAmplifiers] = useState([]);
  const [editingAmplifier, setEditingAmplifier] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [parentAmplifier, setParentAmplifier] = useState(null);
  const [returnTo, setReturnTo] = useState(null); // Track where to return on cancel
  const [filters, setFilters] = useState({
    manufacturer_id: '',
    year_from: '',
    year_to: '',
    model: '',
    description: ''
  });
  
  // Add pagination state similar to AmplifierSearchScreen
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  const readOnlyMode = isReadOnly('/amplifier_screen');

  const fetchAmplifiers = useCallback(async (pageNum = 1) => {
    const params = {
      page: pageNum,
      pageSize: 50  // Use fixed pageSize to avoid dependency issues
    };
    Object.keys(filters).forEach(key => {
      if (filters[key]) params[key] = filters[key];
    });

    try {
      // Use the parent-only endpoint to show only parent amplifiers in the main listing
      const res = await api.get('/amplifiers-parents', { params });

      // Handle paginated response
      if (res.data.data) {
        setAmplifiers(res.data.data);
        setPagination(res.data.pagination);
      } else {
        // Fallback for non-paginated response (shouldn't happen with updated API)
        setAmplifiers(res.data);
        setPagination({
          page: 1,
          pageSize: 50,
          total: res.data.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (error) {
      console.error('Error fetching amplifiers:', error);
      setAmplifiers([]);
      setPagination({
        page: 1,
        pageSize: 50,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    }
  }, [filters]);

  const fetchManufacturers = async () => {
    const res = await manufacturerApi.get('/manufacturers');
    // Ensure each manufacturer has a 'name' property for the dropdown
    const normalized = res.data.map(m => ({
      ...m,
      name: m.name || m.manufacturer_name // fallback if API returns manufacturer_name
    }));
    // Sort alphabetically by name
    normalized.sort((a, b) => a.name.localeCompare(b.name));
    setManufacturers(normalized);
  };

  const fetchTemplates = async () => {
    try {
      const res = await templatesApi.get('/templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Set empty array if templates API is not available
      setTemplates([]);
    }
  };

  const fetchParentAmplifiers = async () => {
    try {
      const res = await api.get('/amplifiers-parents');
      setParentAmplifiers(res.data);
    } catch (error) {
      console.error('Error fetching parent amplifiers:', error);
      setParentAmplifiers([]);
    }
  };

  useEffect(() => {
    fetchManufacturers();
    fetchTemplates();
    fetchParentAmplifiers();
  }, []);

  useEffect(() => {
    fetchAmplifiers();
  }, [fetchAmplifiers]);

  // Handle creating child amplifier from URL params
  useEffect(() => {
    const parentId = searchParams.get('parent_id');
    if (parentId && !editingAmplifier) {
      // Find the parent amplifier and set up for creating a child
      const parent = parentAmplifiers.find(p => p.amplifier_id === parseInt(parentId));
      if (parent) {
        setParentAmplifier(parent);
        setReturnTo(parent.manufacturer_slug && parent.slug ? `/amps/${parent.manufacturer_slug}/${parent.slug}` : `/amplifier-components/${parentId}`); // Return to parent details when creating child
        // Create a child amplifier template with parent data
        const childTemplate = {
          name: `${parent.name} - Individual Unit`,
          manufacturer_id: parent.manufacturer_id,
          template_id: parent.template_id,
          start_year: parent.start_year,
          end_year: parent.end_year,
          model: parent.model,
          description: parent.description,
          is_parent: false,
          parent_id: parentId
        };
        setEditingAmplifier(childTemplate);
        setShowForm(true);
      }
    }
  }, [searchParams, parentAmplifiers, editingAmplifier]);

  // Handle editing amplifier from navigation state
  useEffect(() => {
    if (location.state?.editAmplifier) {
      setEditingAmplifier(location.state.editAmplifier);
      setParentAmplifier(null);
      setShowForm(true);
      
      // If editing a child amplifier, set return path to parent details
      if (location.state.editAmplifier.parent_id) {
        setReturnTo(`/amplifier-components/${location.state.editAmplifier.parent_id}`); // TODO: use slug URL when parent slug is available
      } else {
        setReturnTo('/amplifier_screen'); // Return to list for parent amplifiers
      }
      
      // Clear the navigation state
      navigate('/amplifier_screen', { replace: true });
    }
  }, [location.state, navigate]);

  const handleCreate = () => {
    if (readOnlyMode) return;
    setEditingAmplifier(null);
    setParentAmplifier(null);
    setReturnTo('/amplifier_screen'); // Return to list when creating from list
    setShowForm(true);
  };

  const handleEdit = amp => {
    if (readOnlyMode) return;
    setEditingAmplifier(amp);
    setParentAmplifier(null);
    setReturnTo('/amplifier_screen'); // Always return to list when editing from list
    setShowForm(true);
  };

  const handleDelete = async id => {
    if (readOnlyMode) return;
    if (window.confirm('Are you sure you want to delete this amplifier?')) {
      await api.delete(`/amplifiers/${id}`);
      fetchAmplifiers();
    }
  };

  const handleSave = async formData => {
    if (editingAmplifier && editingAmplifier.amplifier_id) {
      // Editing existing amplifier
      await api.put(`/amplifiers/${editingAmplifier.amplifier_id}`, formData);
    } else {
      // Creating new amplifier
      await api.post('/amplifiers', formData);
    }
    fetchAmplifiers();
    fetchParentAmplifiers();
    setShowForm(false);
    setEditingAmplifier(null);
    setParentAmplifier(null);
    
    // Navigate to the appropriate return location
    if (returnTo) {
      navigate(returnTo);
      setReturnTo(null); // Clear the return path
    } else {
      // Fallback for child creation from URL params
      if (searchParams.get('parent_id')) {
        navigate('/amplifier_screen', { replace: true });
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAmplifier(null);
    setParentAmplifier(null);
    
    // Navigate to the appropriate return location
    if (returnTo) {
      navigate(returnTo);
      setReturnTo(null); // Clear the return path
    } else {
      // Fallback to clearing URL params if we were creating a child
      if (searchParams.get('parent_id')) {
        navigate('/amplifier_screen', { replace: true });
      }
    }
  };

  const handleFilterChange = (field, value) => {
    // Support batch filter changes: if field is an object, merge all keys
    let newFilters;
    if (typeof field === 'object' && field !== null) {
      newFilters = { ...filters, ...field };
    } else {
      newFilters = { ...filters, [field]: value };
    }
    setFilters(newFilters);
    
    // Reset to page 1 when filters change - use newFilters directly
    fetchAmplifiersWithFilters(1, newFilters);
  };

  const fetchAmplifiersWithFilters = useCallback(async (pageNum = 1, filtersToUse = filters) => {
    const params = {
      page: pageNum,
      pageSize: 50
    };
    Object.keys(filtersToUse).forEach(key => {
      if (filtersToUse[key]) params[key] = filtersToUse[key];
    });
    
    try {
      // Use the parent-only endpoint to show only parent amplifiers in the main listing
      const res = await api.get('/amplifiers-parents', { params });

      // Handle paginated response
      if (res.data.data) {
        setAmplifiers(res.data.data);
        setPagination(res.data.pagination);
      } else {
        // Fallback for non-paginated response (shouldn't happen with updated API)
        setAmplifiers(res.data);
        setPagination({
          page: 1,
          pageSize: 50,
          total: res.data.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (error) {
      console.error('Error fetching amplifiers:', error);
      setAmplifiers([]);
      setPagination({
        page: 1,
        pageSize: 50,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    }
  }, [filters]);

  // Add pagination handlers similar to AmplifierSearchScreen
  const handlePageChange = (newPage) => {
    fetchAmplifiersWithFilters(newPage);
  };
  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '2rem 1.5rem'
    }}>
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: '600',
          color: '#333',
          letterSpacing: '-0.02em',
          marginBottom: '1.5rem'
        }}>
          Amplifiers {parentAmplifier && <span style={{ color: '#888', fontWeight: '400' }}>— Creating Individual Unit for {parentAmplifier.name}</span>}
        </h1>
        
        {!showForm && !readOnlyMode && (
          <div style={{ marginBottom: '1.5rem' }}>
            <button 
              onClick={handleCreate}
              style={{
                background: '#2563eb',
                color: 'white',
                border: 'none',
                padding: '0.55rem 1.25rem',
                fontSize: '0.85rem',
                fontWeight: '500',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            >
              + Create New Amplifier
            </button>
          </div>
        )}
        
        {readOnlyMode && (
          <div style={{ 
            padding: '0.75rem 1rem',
            background: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffc107',
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              color: '#856404', 
              fontStyle: 'italic', 
              margin: '0',
              fontSize: '0.9rem'
            }}>
              Read-only mode: You can view but not modify amplifiers
            </p>
          </div>
        )}
        
        {showForm && !readOnlyMode && (
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '1.5rem',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            marginBottom: '1.5rem'
          }}>
            <AmplifierForm
              amplifier={editingAmplifier}
              manufacturers={manufacturers}
              templates={templates}
              parentAmplifiers={parentAmplifiers}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </div>
        )}
        
        {!showForm && (
          <>
            {/* Results Summary */}
            <div style={{ 
              marginBottom: '1rem',
              color: '#888',
              fontSize: '0.85rem'
            }}>
              Found <strong style={{ color: '#333' }}>{pagination.total}</strong> amplifier{pagination.total !== 1 ? 's' : ''}
              {pagination.total > pagination.pageSize && (
                <span> (page {pagination.page} of {pagination.totalPages})</span>
              )}
            </div>
            
            {/* Amplifier List */}
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '1.5rem',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}>
              <AmplifierList
                amplifiers={amplifiers}
                filters={filters}
                onFilterChange={handleFilterChange}
                manufacturers={manufacturers}
                onEdit={readOnlyMode ? null : handleEdit}
                onDelete={readOnlyMode ? null : handleDelete}
                readOnly={readOnlyMode}
                hideDescription={true}
                renderExtraActions={amp => (
                  <button
                    onClick={() => window.open(amp.manufacturer_slug && amp.slug ? `/amps/${amp.manufacturer_slug}/${amp.slug}` : `/amplifier-components/${amp.amplifier_id}`, '_blank')}
                    style={{
                      background: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.4rem 0.85rem',
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                      fontWeight: '500',
                      transition: 'box-shadow 0.15s ease',
                      marginLeft: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    Details
                  </button>
                )}
              />
              
              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div style={{
                  marginTop: '1.5rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e9ecef',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  <button onClick={() => handlePageChange(1)} disabled={!pagination.hasPrev}
                    style={{ padding: '0.4rem 0.85rem', borderRadius: '6px', border: '1px solid #ddd', background: !pagination.hasPrev ? '#f5f5f5' : 'white', color: !pagination.hasPrev ? '#bbb' : '#333', cursor: !pagination.hasPrev ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '0.82rem' }}>
                    First
                  </button>
                  <button onClick={() => handlePageChange(pagination.page - 1)} disabled={!pagination.hasPrev}
                    style={{ padding: '0.4rem 0.85rem', borderRadius: '6px', border: '1px solid #ddd', background: !pagination.hasPrev ? '#f5f5f5' : 'white', color: !pagination.hasPrev ? '#bbb' : '#333', cursor: !pagination.hasPrev ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '0.82rem' }}>
                    Previous
                  </button>
                  <span style={{ padding: '0.4rem 1rem', color: '#666', fontSize: '0.85rem' }}>
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button onClick={() => handlePageChange(pagination.page + 1)} disabled={!pagination.hasNext}
                    style={{ padding: '0.4rem 0.85rem', borderRadius: '6px', border: '1px solid #ddd', background: !pagination.hasNext ? '#f5f5f5' : 'white', color: !pagination.hasNext ? '#bbb' : '#333', cursor: !pagination.hasNext ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '0.82rem' }}>
                    Next
                  </button>
                  <button onClick={() => handlePageChange(pagination.totalPages)} disabled={!pagination.hasNext}
                    style={{ padding: '0.4rem 0.85rem', borderRadius: '6px', border: '1px solid #ddd', background: !pagination.hasNext ? '#f5f5f5' : 'white', color: !pagination.hasNext ? '#bbb' : '#333', cursor: !pagination.hasNext ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '0.82rem' }}>
                    Last
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AmplifierScreen;